import { Module, Plugin, ActionContext } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import { getTickets, getTicketTaxes, getTicket, getTicketsExcel} from '../../services/api';
import { TICKET_TAXES } from '@growmark/energy-llc-api/types/dist/src/common/types/tax';
import { LOCATION } from '@growmark/energy-llc-api/types/dist/src/common/types/location';
import { PRODUCTS } from '@growmark/energy-llc-api/types/dist/src/common/types/product';
import { TICKET_DETAILS } from '@growmark/energy-llc-api/types';
import { TICKETS } from '@growmark/energy-llc-api/types/dist/src/member/types/ticket';
import { UI_TICKET } from '~/types';
import { interruptable } from '@growmark/coroutine';
import { useSnackbar } from '../../../../composables/useSnackbar'
const { show } = useSnackbar()

// Helper to get last week's date range as default
function getLastWeekRange(): string[] {
  const today = new Date();
  const endDate = new Date(today);
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 7);
  
  const formatDate = (d: Date) => d.toISOString().split('T')[0];
  return [formatDate(startDate), formatDate(endDate)];
}

function buildTicketsQueryParams(state: TicketActionContext["state"]): any {
  const params: any = {
    offset: state.offset,
    pageSize: state.pageSize,
    startDate: state.filterDate?.[0],
    endDate: state.filterDate?.[1]
  };

  if (state.searchTicketString) {
    params.ticketRef = state.searchTicketString;
  }
  if (state.selectedProductFilter) {
    params.ticketProduct = state.selectedProductFilter.id;
  }
  if (state.selectedLocationFilter) {
    params.location = state.selectedLocationFilter.name_key;
  }
  if (state.selectedStatusFilter.length > 0) {
    const statuses = [...state.selectedStatusFilter];
    if (statuses.includes(4) && !statuses.includes(5)) statuses.push(5);
    params.status = statuses.join(',');
  }

  return params;
}

const state = {
  offset: 0,
  pageSize: 50,
  uiTickets: [] as UI_TICKET[],
  currentTicket: null as UI_TICKET | null,
  ticketDetails: [] as TICKET_DETAILS[],
  loadingTicketDetails: false,
  taxes: [] as TICKET_TAXES[],
  loadingTaxes: false,
  vendorTaxes: [] as any[],
  loadingVendorTaxes: false,
  loading: false,
  clientStatusFilter: [] as number[],
  filterDate: [] as string[],
  viewMode: 'card', // 'card' or 'table'
  error: null as any,
  selectedProductFilter: null as PRODUCTS | null,
  products: [] as PRODUCTS[],
  selectedLocationFilter: null as LOCATION | null,
  locationFilters: [] as LOCATION[],
  selectedStatusFilter: [] as number[],
  searchTicketString: '',
};

const actions = {
  setClientStatusFilter({ commit }: TicketActionContext, status: number[]) {
    commit('SET_CLIENT_STATUS_FILTER', status);
  },
  setFilterDate({ commit, dispatch }: TicketActionContext, date: string[]) {
    commit('SET_FILTER_DATE', date);
    dispatch('fetchTickets');
  },
  toggleViewMode({ commit, state }: TicketActionContext) {
    const newMode = state.viewMode === 'card' ? 'table' : 'card';
    commit('SET_VIEW_MODE', newMode);
  },
  // Get a specific ticket by ticket reference from local state
  fetchTicketById({ commit, state, dispatch }: TicketActionContext, ticketId: string) {
    const ticket = state.uiTickets.find((t: UI_TICKET) => t.ticket_ref === ticketId);
    if (ticket) {
      commit('SET_CURRENT_TICKET', JSON.parse(JSON.stringify(ticket))); // Clone to avoid direct state mutation
    } else {
      commit('SET_CURRENT_TICKET', null);
    }
  },
  // Fetch a specific ticket by ID from the API and set it as current ticket
  async fetchTicketByIdFromAPI({ commit }: TicketActionContext, ticketId: string) {
    try {
      const ticketData = await getTicket(ticketId);
      if (ticketData && ticketData.length > 0) {
        const ticket = ticketData[0]; // Get the first ticket from the array
        commit('SET_CURRENT_TICKET', ticket);
        return ticket;
      } else {
        show('Ticket not found', { type: 'error' });
        return null;
      }
    } catch (error) {
      show('Error fetching ticket details. Please try again.', { type: 'error' });
      return null;
    }   
  },
  fetchTickets: interruptable(function*({ commit, state }: TicketActionContext) {
    commit('SET_LOADING', true);
    yield new Promise((resolve) => setTimeout(resolve, 300)); // Debounce delay
    commit('SET_UI_TICKETS', []); // Clear existing tickets before fetching new ones
    commit('SET_OFFSET', 0);
    commit('SET_PAGE_SIZE', 50);
    try {
      const params = buildTicketsQueryParams(state);
      
      const data = (yield getTickets(params)) as TICKETS[];
      const uiTickets = data.map((ticket: TICKETS) => ({
        ...ticket,
        id: uuidv4(),
        loading: false
      }));

      commit('SET_UI_TICKETS', uiTickets); 
    } catch (error) {
      show('Error fetching tickets. Please try again.', { type: 'error' });
    } finally {
      commit('SET_LOADING', false);
    }
  }),
  async fetchMoreTickets({ commit, state }: TicketActionContext) {
    try {
      const params = buildTicketsQueryParams(state);
      
      const data = await getTickets(params);
      const uiTickets = data.map((ticket: TICKETS) => ({
        ...ticket,
        id: uuidv4(),
        loading: false
      }));

      commit('SET_MORE_UI_TICKETS', uiTickets); 
    } catch (error) {
      show('Error fetching more tickets. Please try again.', { type: 'error' });
    }   
  },
  async getTicketsExcel({ commit, state }: TicketActionContext) {
    try {
      const params = buildTicketsQueryParams(state);
      if (!params.status && state.clientStatusFilter.length > 0) {
        const statuses = [...state.clientStatusFilter];
        if (statuses.includes(4) && !statuses.includes(5)) statuses.push(5);
        if (statuses.includes(10) && !statuses.includes(11)) statuses.push(11);
        params.status = statuses.join(',');
      }
      const response = await getTicketsExcel(params);
      return response;
    } catch (error) {
      show('Error downloading tickets Excel. Please try again.', { type: 'error' });
    }   
  },
  // Update a ticket
  updateTicket({ commit, state }: TicketActionContext, updatedTicket: UI_TICKET) {
    // In a real app, this would make an API call
    const index = state.uiTickets.findIndex((t: UI_TICKET) => t.ticket_ref === updatedTicket.ticket_ref);
    if (index !== -1) {
      const newTickets = [...state.uiTickets];
      newTickets[index] = updatedTicket;
      commit('SET_TICKETS', newTickets);
      commit('SET_CURRENT_TICKET', updatedTicket);
    }
  },
  
  async getTicketTaxes({ commit, state }: TicketActionContext, payload: { ticketId: string; client: string; account: string; location: string }) {
    try {
      commit('SET_LOADING_TAXES', true);
      const taxes = await getTicketTaxes(payload.ticketId, payload.client, payload.account, payload.location);
      commit('SET_TICKET_TAXES', taxes);
    } catch (error) {
      console.error('Error fetching ticket taxes:', error); 
    } finally {
      commit('SET_LOADING_TAXES', false);
    }
  },
  async getVendorTaxing({ commit }: TicketActionContext, payload: { client: string; account: string; location: string }) {
    try {
      commit('SET_LOADING_VENDOR_TAXES', true);
      const { getVendorTaxing } = await import('../../services/api');
      const vendorTaxes = await getVendorTaxing(payload.client, payload.account, payload.location);
      commit('SET_VENDOR_TAXES', vendorTaxes);
    } catch (error) {
      console.error('Error fetching vendor taxing:', error);
    } finally {
      commit('SET_LOADING_VENDOR_TAXES', false);
    }
  },
  async fetchTicketDetails({ commit }: TicketActionContext, ticketId: string) {
    try {
      commit('SET_TICKET_DETAILS_LOADING', true)
      const { getTicketDetails } = await import('../../services/api');
      const details = await getTicketDetails(ticketId);
      commit('SET_TICKET_DETAILS', details);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    } finally {
      commit('SET_TICKET_DETAILS_LOADING', false)
    }
  },
  // Create a new ticket
  async createTicket({ commit, state }: TicketActionContext, oldTicket: string) {
    const id = uuidv4();
    const ticket = {
      loading: true,
      id: id,
    };
    const newTickets = [ticket, ...state.uiTickets];
    commit('SET_UI_TICKETS', newTickets);
    try {
      const { postTicket } = await import('../../services/api');
      const newTicket = await postTicket(oldTicket); 
      const nTicket = { loading: false, ...newTicket, id: ticket.id }
      commit('SET_UI_TICKET', nTicket);
      return nTicket;
    } catch (error) {
      console.error('Error creating ticket:', error);
      commit('DELETE_UI_TICKET', id);
      show('Error creating ticket. Please try again.', { type: 'error' });
      return null;
    }
  },
  // Update a ticket via PUT API
  async putTicket({ commit, state }: TicketActionContext, payload: { ticketRef: string; client: string; fields: Record<string, unknown> }) {
    try {
      const { putTicket } = await import('../../services/api');
      const result = await putTicket(payload.ticketRef, {
        client: payload.client,
        ...payload.fields
      });
      // Refresh the ticket data from the API after a successful update
      const { getTicket } = await import('../../services/api');
      const updatedData = await getTicket(payload.ticketRef);
      if (updatedData && updatedData.length > 0) {
        const updatedTicket = { ...updatedData[0], id: state.currentTicket?.id || uuidv4(), loading: false };
        commit('SET_CURRENT_TICKET', updatedTicket);
        // Also update in uiTickets list
        const index = state.uiTickets.findIndex((t: UI_TICKET) => t.ticket_ref === payload.ticketRef);
        if (index !== -1) {
          const newTickets = [...state.uiTickets];
          newTickets[index] = updatedTicket;
          commit('SET_UI_TICKETS', newTickets);
        }
      }
      return result;
    } catch (error) {
      console.error('Error updating ticket:', error);
      show('Error updating ticket. Please try again.', { type: 'error' });
      throw error;
    }
  },
  
  // Filter actions
  setSelectedProductFilter({ commit, dispatch }: TicketActionContext, product: PRODUCTS | null) {
    commit('SET_SELECTED_PRODUCT_FILTER', product);
    dispatch('fetchTickets');
  },
  
  setProductFilters({ commit }: any, products: PRODUCTS[]) {
    commit('SET_PRODUCT_FILTERS', products);
  },
  
  setSelectedLocationFilter({ commit, dispatch }: TicketActionContext, location: LOCATION | null) {
    commit('SET_SELECTED_LOCATION_FILTER', location);
    dispatch('fetchTickets');
  },
  
  setLocationFilters({ commit }: TicketActionContext, locations: LOCATION[]) {
    commit('SET_LOCATION_FILTERS', locations);
  },
  
  setSelectedStatusFilter({ commit, dispatch }: TicketActionContext, status: number[]) {
    commit('SET_SELECTED_STATUS_FILTER', status);
    dispatch('fetchTickets');
  },
  
  setSearchTicketString({ commit, dispatch, state }: TicketActionContext, searchString: string) {
    commit('SET_SEARCH_TICKET_STRING', searchString);
    dispatch('fetchTickets');
  },
  
  clearAllFilters({ commit, dispatch }: TicketActionContext) {
    commit('SET_SELECTED_PRODUCT_FILTER', null);
    commit('SET_SELECTED_LOCATION_FILTER', null);
    commit('SET_SELECTED_STATUS_FILTER', []);
    commit('SET_SEARCH_TICKET_STRING', '');
    commit('SET_FILTER_DATE', []);
    dispatch('fetchTickets');
  },
  
  async fetchProducts({ commit }: TicketActionContext) {
    try {
      const { getProducts } = await import('../../services/api');
      const products = await getProducts();
      commit('SET_PRODUCT_FILTERS', products);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  },
  
  async fetchLocations({ commit }: TicketActionContext, customer?: string) {
    try {
      const { getLocations } = await import('../../services/api');
      const locations = await getLocations(customer);
      commit('SET_LOCATION_FILTERS', locations);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  },
  setOffset({ commit }: TicketActionContext, offset: number) {
    commit('SET_OFFSET', offset);
  },
  setTicket({ commit }: TicketActionContext, updatedTicket: TICKETS) {
    commit('SET_TICKET', updatedTicket);
  },
  setUITicketLoading({ commit }: TicketActionContext, payload: { ticketRef: string; loading: boolean }) {
    const { ticketRef, loading } = payload;
    commit('SET_UI_TICKET', { ...state.uiTickets.find((t: UI_TICKET) => t.ticket_ref === ticketRef), loading });
  }
};

const getters = {
  filteredTickets: (state: State) => {
    // Since we're now doing server-side filtering in fetchTickets,
    // this getter primarily returns the tickets as-is, but can provide
    // additional client-side filtering if needed
    let filtered = [...state.uiTickets];
    
    // Apply any additional client-side filtering that wasn't handled server-side
    if (state.clientStatusFilter.length > 0 && state.selectedStatusFilter.length === 0) {
      const expandedStatuses = [...state.clientStatusFilter];
      if (expandedStatuses.includes(4) && !expandedStatuses.includes(5)) expandedStatuses.push(5);
      if (expandedStatuses.includes(10) && !expandedStatuses.includes(11)) expandedStatuses.push(11);
      filtered = filtered.filter((ticket: UI_TICKET) => expandedStatuses.includes(ticket.wireless_status));
    }
    return filtered;
  },
  getUITickets: (state: State) => state.uiTickets,
  filteredUITickets: (state: State) => {
    let filtered = [...state.uiTickets];
    
    if (state.clientStatusFilter.length > 0 && state.selectedStatusFilter.length === 0) {
      const expandedStatuses = [...state.clientStatusFilter];
      if (expandedStatuses.includes(4) && !expandedStatuses.includes(5)) expandedStatuses.push(5);
      if (expandedStatuses.includes(10) && !expandedStatuses.includes(11)) expandedStatuses.push(11);
      filtered = filtered.filter((ticket: UI_TICKET) => expandedStatuses.includes(ticket.wireless_status));
    }
    
    return filtered;
  },
  viewMode: (state: State) => state.viewMode,
  currentTicket: (state: State) => state.currentTicket,
  ticketById: (state: State) => (id: string) => {
    return state.uiTickets.find((ticket: UI_TICKET) => ticket.ticket_ref === id);
  },
  clientStatusFilter: (state: State) => state.clientStatusFilter,
  filterDate: (state: State) => state.filterDate,
  taxes: (state: State) => state.taxes,
  loadingTaxes: (state: State) => state.loadingTaxes,
  vendorTaxes: (state: State) => state.vendorTaxes,
  loadingVendorTaxes: (state: State) => state.loadingVendorTaxes,
  error: (state: State) => state.error,
  selectedProductFilter: (state: State) => state.selectedProductFilter,
  products: (state: State) => state.products,
  selectedLocationFilter: (state: State) => state.selectedLocationFilter,
  locationFilters: (state: State) => state.locationFilters,
  selectedStatusFilter: (state: State) => state.selectedStatusFilter,
  searchTicketString: (state: State) => state.searchTicketString,
  dateFilters: (state: State) => state.filterDate,
  hasActiveFilters: (state: State) => {
    return !!(
      state.selectedProductFilter ||
      state.selectedLocationFilter ||
      state.selectedStatusFilter.length > 0 ||
      state.searchTicketString ||
      state.filterDate.length > 0
    );
  },
  ticketDetails: (state: State) => state.ticketDetails,
  loading: (state: State) => state.loading,
  loadingTicketDetails: (state: State) => state.loadingTicketDetails,
  offset: (state: State) => state.offset,
  pageSize: (state: State) => state.pageSize,
};

const mutations = {
  SET_UI_TICKETS: (state: State, uiTickets: UI_TICKET[]) => { state.uiTickets = uiTickets },
  SET_MORE_UI_TICKETS: (state: State, uiTickets: UI_TICKET[]) => { state.uiTickets = [...state.uiTickets, ...uiTickets] },
  SET_UI_TICKET(state: State, updatedTicket: UI_TICKET) {
    const index = state.uiTickets.findIndex(t => t.id === updatedTicket.id);
    if (index !== -1) {
      state.uiTickets.splice(index, 1, updatedTicket);
    }
  },
  DELETE_UI_TICKET(state: State, ticketId: string) {
    const index = state.uiTickets.findIndex(t => t.id === ticketId);
    if (index !== -1) {
      state.uiTickets.splice(index, 1);
    }
  },
  SET_CLIENT_STATUS_FILTER(state: State, status: number[]) {
    state.clientStatusFilter = status;
  },
  SET_FILTER_DATE(state: State, date: string[]) {
    state.filterDate = date;
  },
  SET_VIEW_MODE(state: State, mode: string) {
    state.viewMode = mode;
  },
  SET_CURRENT_TICKET(state: State, ticket: UI_TICKET | null) {
    state.currentTicket = ticket;
  },
  SET_LOADING(state: State, loading: boolean) {
    state.loading = loading;
  },
  SET_TICKET_TAXES(state: State, taxes: TICKET_TAXES[]) {
    state.taxes = taxes;  
  },
  SET_TICKET_DETAILS(state: State, details: TICKET_DETAILS[]) {
    state.ticketDetails = details;  
  },
  SET_SELECTED_PRODUCT_FILTER(state: State, product: PRODUCTS | null) {
    state.selectedProductFilter = product;
  },
  SET_PRODUCT_FILTERS(state: State, products: PRODUCTS[]) {
    state.products = products;
  },
  SET_SELECTED_LOCATION_FILTER(state: State, location: LOCATION | null) {
    state.selectedLocationFilter = location;
  },
  SET_LOCATION_FILTERS(state: State, locations: LOCATION[]) {
    state.locationFilters = locations;
  },
  SET_SELECTED_STATUS_FILTER(state: State, status: number[]) {
    state.selectedStatusFilter = status;
  },
  SET_SEARCH_TICKET_STRING(state: State, searchString: string) {
    state.searchTicketString = searchString;
  },
  SET_OFFSET(state: State, offset: number) {
    state.offset = offset;
  },
  SET_PAGE_SIZE(state: State, pageSize: number) {
    state.pageSize = pageSize;
  },
  SET_TICKET_DETAILS_LOADING(state: State, isLoading: boolean) {
    state.loadingTicketDetails = isLoading
  },
  SET_LOADING_TAXES(state: State, isLoading: boolean) {
    state.loadingTaxes = isLoading
  },
  SET_VENDOR_TAXES(state: State, vendorTaxes: any[]) {
    state.vendorTaxes = vendorTaxes;
  },
  SET_LOADING_VENDOR_TAXES(state: State, isLoading: boolean) {
    state.loadingVendorTaxes = isLoading;
  },
  SET_TICKET(state: State, updatedTicket: TICKETS) {
    const index = state.uiTickets.findIndex(t => t.ticket_ref === updatedTicket.ticket_ref);
    if (index !== -1) {
      state.uiTickets.splice(index, 1, { ...state.uiTickets[index], ...updatedTicket });
    }
    if (state.currentTicket?.ticket_ref === updatedTicket.ticket_ref) {
      state.currentTicket = { ...state.currentTicket, ...updatedTicket };
    }
  }
};

//js exports
export const NAMESPACE = 'tickets';
type TicketActionContext = ActionContext<State, MinimalRootState>
export const plugin: Plugin<State> = async store => {
  // Load initial filter options
  store.dispatch(`${NAMESPACE}/fetchProducts`);
  store.dispatch(`${NAMESPACE}/fetchLocations`);
  
  store.subscribe(mutation => {
    switch(mutation.type){
      default: return;
    }
  });
};
export const module: Module<State, MinimalRootState> = {
  namespaced: true,
  state, getters, actions, mutations
};

//types
type MinimalRootState = object;
export type State = typeof state;
export type Actions = typeof actions;
export type Getters = typeof getters;
export type Mutations = typeof mutations;
